import React, { useState } from 'react';
import { AppProps } from 'next/app';
import { SessionProvider } from 'next-auth/react';
import InternalSessionProvider from '../providers/SessionProvider';
import { Session } from 'next-auth';
import NotificationGroup from '../components/chat/NotificationGroup';
import { AppStateProvider, useAppState } from '../providers/AppStateProvider';
import { UserPermissionProvider } from '../providers/UserPermissionProvider';
import { IdentityProvider } from '../providers/IdentityProvider';
import Auth from '../components/auth/Auth';
import { ThemeProvider } from 'styled-components';
import { PropsWithChildren, ReactElement, ReactNode, useEffect } from 'react';
import Layout from '../components/common/Layout';
import mockSession from '../data/mock-session.json';
import LineProvider from '../providers/LineProvider';
import { CurrentChatMessagesProvider } from '../providers/ChatMessagesProvider/CurrentChatMessagingProvider';
import NoSsr from '../components/common/NoSsr';
import { useRouter } from 'next/router';
import { GlobalStyles, darkTheme, lightTheme } from '../styles/chime-react';
import { NotificationProvider } from '../providers/NotificationProvider';
import dynamic from 'next/dynamic';
import { PageWithType } from '../types';
import { AppTypes } from '../constants';
import NoneLayout from '../components/common/NoneLayout';
import Meta from '../components/common/Meta';
import { LoadingProvider } from '../providers/LoadingProvider';
import { Loading } from '../components/common/elements/Loading';
import { AgreementProvider } from '../providers/AgreementProvider';
import { KinesisProvider } from '../providers/KinesisProvider';
import { TimeProvider } from '../providers/TimeProvider';
import { useIsUnderMaintenance } from '../hooks/useIsUnderMaintenance';
import LiffLayout from '../components/common/LiffLayout';
import Maintenance from '../components/maintenance/Maintenance';
import { usePreventScrollOnFocus } from '../hooks/usePreventScrollOnFocus';
import { isAndroid } from '../utils/platform.utils';
// import { options } from '../constants/newrelic';
const AutoModal = dynamic(() => import('../components/common/AutoModal'));
const MessagingProvider = dynamic(
  () => import('../providers/ChatMessagesProvider/MessagingProvider')
);

export type NextComponentType<P = {}, IP = P> = PageWithType<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
  useLocalSession?: boolean;
  chatType?: string;
  public?: boolean;
};
const logVersion = (log: (msg: string) => void) =>
  log(`buildNo:${process.env.CONFIG_BUILD_ID}`);

type AppPropsWithComplements<T> = AppProps<T> & {
  Component: NextComponentType<T>;
};
if (process.env.NEXT_PUBLIC_CONSOLE === '1' && typeof window !== 'undefined') {
  import('vconsole').then(({ default: VConsole }) => {
    const vConsole = new VConsole({ theme: 'dark' });
    logVersion((message) => vConsole.system.log(message));
    vConsole.setSwitchPosition(20, 200);
  });
}

function App({
  Component,
  pageProps
}: AppPropsWithComplements<{
  session: Session;
  localSession?: { idToken: string; profile: object };
}>) {
  useEffect(() => {
    logVersion(console.log);
  }, []);
  useEffect(() => {
    if (window.visualViewport) {
      const resize = () => {
        document.body.style.height = `${window.visualViewport?.height || 0}px`;
        document.body.style.top = `${window.visualViewport?.offsetTop || 0}px`;
      };
      const resizeHandler = () => {
        resize();
        // Resizeイベントのタイミングがブラウザーによって異なり、offsetTopが更新される前に発火される場合があります。
        setTimeout(resize, 200);
      };

      window.visualViewport.addEventListener('resize', resizeHandler);

      return () => {
        window.visualViewport?.removeEventListener('resize', resizeHandler);
      };
    }
  }, []);

  const mock = process.env.NEXT_PUBLIC_MOCK;
  const session = mock ? mockSession : pageProps.session;

  const { query, replace } = useRouter();
  const appType = process.env.NEXT_PUBLIC_APP_TYPE as AppTypes;
  const getLayout =
    Component.getLayout ??
    ((page) =>
      isPagePublic ? (
        <NoneLayout page={<Component {...pageProps} />} />
      ) : (
        defaultLayout(page)
      ));
  const isAppPage =
    !Component.appTypes?.length || Component.appTypes.includes(appType);
  const isPagePublic = !!Component.public;
  const useLocalSession =
    !!pageProps.localSession ||
    (process.env.NEXT_PUBLIC_DISABLE_LOCAL_SESSION !== '1' &&
      ((!!query['liff.state'] || Component.useLocalSession) ?? false));
  const chatType = Component.chatType ?? 'chat';
  const noChat = chatType === 'none';
  const isUnderMaintenance = useIsUnderMaintenance();
  usePreventScrollOnFocus(useLocalSession);

  if (isUnderMaintenance) {
    return (
      <AppStateProvider useLocalSession={useLocalSession}>
        <Meta useLocalSession={useLocalSession} />
        <Theme>
          <LoadingProvider>
            <Loading />
            <NotificationProvider>
              {useLocalSession ? (
                <LiffLayout page={<Maintenance />} />
              ) : (
                <NoneLayout page={<Maintenance />} />
              )}
              <AutoModal />
            </NotificationProvider>
          </LoadingProvider>
        </Theme>
      </AppStateProvider>
    );
  }

  if (!isAppPage) {
    replace('/404');
    return <></>;
  }
  if (isPagePublic) {
    const content = () => (
      <Theme>
        <LoadingProvider>
          <Loading />
          <NotificationProvider>
            {getLayout(<Component {...pageProps} />)}
            <AutoModal />
          </NotificationProvider>
        </LoadingProvider>
      </Theme>
    );

    return (
      <AppStateProvider useLocalSession={useLocalSession}>
        <Meta useLocalSession={useLocalSession} />
        {useLocalSession ? (
          pageProps.localSession ? (
            content()
          ) : (
            <NoSsr>{content()}</NoSsr>
          )
        ) : (
          <SessionProvider session={session} refetchOnWindowFocus={false}>
            {content()}
          </SessionProvider>
        )}
      </AppStateProvider>
    );
  }

  const page = () => (
    <>
      <KinesisProvider>
        <AgreementProvider>
          {getLayout(<Component {...pageProps} />)}
        </AgreementProvider>
      </KinesisProvider>
      <div
        style={{
          position: 'absolute',
          right: 0,
          bottom: 0,
          zIndex: 1
        }}
      >
        <NotificationGroup />
      </div>
    </>
  );
  const content = () => (
    <InternalSessionProvider useLocalSession={useLocalSession} noLogin={noChat}>
      <Theme>
        <LoadingProvider>
          <Loading />
          <Auth>
            {noChat ? (
              page()
            ) : (
              <NotificationProvider>
                <LineProvider>
                  <IdentityProvider>
                    <TimeProvider>
                      <MessagingProvider
                        useLocalSession={useLocalSession}
                        loadLineChannels={true}
                      >
                        <CurrentChatMessagesProvider type={chatType}>
                          <UserPermissionProvider>
                            {page()}
                            <AutoModal />
                          </UserPermissionProvider>
                        </CurrentChatMessagesProvider>
                      </MessagingProvider>
                    </TimeProvider>
                  </IdentityProvider>
                </LineProvider>
              </NotificationProvider>
            )}
          </Auth>
        </LoadingProvider>
      </Theme>
    </InternalSessionProvider>
  );

  return (
    <AppStateProvider useLocalSession={useLocalSession}>
      <Meta useLocalSession={useLocalSession} />
      {useLocalSession ? (
        pageProps.localSession ? (
          content()
        ) : (
          <NoSsr>{content()}</NoSsr>
        )
      ) : (
        <SessionProvider session={session} refetchOnWindowFocus={false}>
          {content()}
        </SessionProvider>
      )}
    </AppStateProvider>
  );
}
const Theme = ({ children }: PropsWithChildren<unknown>) => {
  const { theme } = useAppState();

  return (
    <ThemeProvider theme={theme === 'light' ? lightTheme : darkTheme}>
      <GlobalStyles />
      {children}
    </ThemeProvider>
  );
};

const defaultLayout = (page: ReactElement) => {
  return <Layout page={page} />;
};

export default App;
